import { formatPercentage } from '@helpers/utils/numbers/numbers';
import * as d3 from 'd3';
import { MouseEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFloatingLegend } from '../bar-chart/hooks/use-floating-legend/useFloatingLegend';
import { HighLowBar } from '../high-low-chart/components/high-low-bar/HighLowBar';
import {
  ClipAreas,
  RIGHT_Y_AXIS_WIDTH,
} from './components/clip-areas/ClipAreas';
import { CumulativeReturn } from './components/cumulative-return/CumulativeReturn';
import { HorizontalGridLines } from './components/horizontal-grid-lines/HorizontalGridLines';
import { HighLowTripleLineChartTooltip } from './components/tooltip/HighLowTripleLineChartTooltip';
import { TripleLines } from './components/triple-lines/TripleLines';
import { VerticalLines } from './components/vertical-grid-lines/VerticalGridLines';
import { LineProp } from './components/vertical-grid-lines/VerticalGridLines.types';
import styles from './HighLowTripleLineChart.module.scss';
import {
  HighLowDataPoint,
  HighLowDataPointWithIndex,
} from './HighLowTripleLineChart.types';
import { getPlots } from './utils/gradient-utils/gradient-utils';

export interface HighLowTripleLineChartProps {
  /** Time series of `{ low, median, high }` points with indices. */
  ts: HighLowDataPointWithIndex[];
  /** Horizon label that the "best" highlight + selection styling is applied to. */
  bestHorizon?: string;
  /** Maps horizon labels (e.g. `"5d"`) to indices in `ts` for vertical guide lines. */
  horizonToIndex?: Map<string, number>;
  /** Horizon labels to render as vertical guide lines (others fall back to the muted treatment). */
  highlightedHorizons?: string[];
  /** Highlighted horizons that should still draw their guide line but without a label. */
  hiddenHighlightedHorizonLabels?: string[];
  /** Format axis labels in basis points (`bps`) instead of percentages. */
  isFixedIncome?: boolean;
  /** Render the chart with a bordered frame. Defaults to `false`. */
  isBordered?: boolean;
  /** Use the narrower right-axis treatment (smaller text + label width). Defaults to `false`. */
  useSimpleRightAxis?: boolean;
  /** Pin a maximum chart height (px); the SVG resizes to fit. */
  maxHeight?: number;
  /** Optional title rendered alongside the y-axis. */
  yAxisLabel?: string;
  /** Translated labels keyed by `HighLowDataPoint` field, used inside the hover tooltip. */
  tooltipLabels: Record<keyof HighLowDataPoint, string>;
  /** Default label shown for the days indicator when no point is hovered. */
  daysLabel: string;
  /** Reserved for dynamic day-count labelling — currently unused by the implementation. */
  useDynamicDaysLabel?: boolean;
  /** Cumulative-return overlay points (the "snake" line drawn through the band). */
  snakeTs: { index: number; value: number }[];
}

const RIGHT_Y_AXIS_TEXT_WIDTH = 65;
const SIMPLE_RIGHT_Y_AXIS_TEXT_WIDTH = 45;
const SIMPLE_RIGHT_Y_AXIS_WIDTH = 45;
const ADDITIONAL_HEIGHT_BUFFER = 45;

/**
 * Three-line "fan" chart: low / median / high time-series envelopes
 * with a cumulative-return "snake" overlaid on top.
 *
 * Used in earnings / scenario forecasting widgets where the band shows
 * the modelled distribution and the snake shows actual cumulative
 * return through the same window. Hovering reveals a point-in-time
 * tooltip with all three envelope values plus the snake value.
 */
export const HighLowTripleLineChart = ({
  ts,
  bestHorizon,
  horizonToIndex,
  highlightedHorizons,
  hiddenHighlightedHorizonLabels = [],
  isFixedIncome,
  maxHeight,
  yAxisLabel,
  tooltipLabels,
  daysLabel,
  isBordered = false,
  useSimpleRightAxis = false,
  snakeTs,
}: HighLowTripleLineChartProps) => {
  const rightAxisWidth =
    isBordered || useSimpleRightAxis
      ? SIMPLE_RIGHT_Y_AXIS_TEXT_WIDTH
      : RIGHT_Y_AXIS_TEXT_WIDTH;
  const rightYAxisWidth =
    isBordered || useSimpleRightAxis
      ? SIMPLE_RIGHT_Y_AXIS_WIDTH
      : RIGHT_Y_AXIS_WIDTH;

  const [{ width, height }, setChartSize] = useState({
    width: 0,
    height: 0,
  });
  const maxHeightRef = useRef(maxHeight);
  maxHeightRef.current = maxHeight;
  const [mouseX, setMouseX] = useState<number>();
  const svgRef = useRef<SVGSVGElement>(null);
  const { t } = useTranslation('chart');

  const uniqueId = useId();
  const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } =
    useFloatingLegend({ offsetOptions: 5 });

  useEffect(() => {
    const el = svgRef.current;
    if (!el) {
      return;
    }

    let rafId = 0;

    const updateSize = () => {
      const { width: svgWidth, height: svgHeight } = el.getBoundingClientRect();
      const mh = maxHeightRef.current;
      const nextHeight = mh != null ? mh + ADDITIONAL_HEIGHT_BUFFER : svgHeight;

      setChartSize(prevState => {
        const widthChanged = Math.abs(prevState.width - svgWidth) > 0.5;
        const heightChanged = Math.abs(prevState.height - nextHeight) > 0.5;

        if (!widthChanged && !heightChanged) {
          return prevState;
        }
        return { width: svgWidth, height: nextHeight };
      });
    };

    updateSize();

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updateSize();
      });
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [maxHeight]);

  const domain = [0, ts.length - 1];
  const mainHeight = height * 0.8;
  const plotWidth = Math.max(width - rightAxisWidth, 0);
  const xScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain(domain)
      .range([0, Math.max(width - rightYAxisWidth, 0)]);
  }, [width, domain]);

  const yScale = useMemo(() => {
    const snakeValues = snakeTs.map(d => d.value);
    const tsData = ts.slice(domain[0], domain[1]).flatMap(i => [i.low, i.high]);
    const allData = snakeValues.concat(tsData);

    const yDomain = d3.extent(allData) as [number, number];
    return d3.scaleLinear().domain(yDomain).range([mainHeight, 0]).clamp(true);
  }, [height, ts, snakeTs, domain]);

  const plots = useMemo(() => getPlots(ts), [ts, yScale]);

  const verticalLines = useMemo(() => {
    const results: LineProp[] = [];

    horizonToIndex?.forEach((tsIndex, horizon) => {
      const isHighlightedHorizon = highlightedHorizons?.includes(horizon);
      const isMuted = !isHighlightedHorizon;
      const isSelected = horizon === bestHorizon;
      const variant = isSelected ? 'selected' : isMuted ? 'muted' : undefined;

      results.push({
        x: tsIndex ?? 0,
        label:
          isHighlightedHorizon &&
          !hiddenHighlightedHorizonLabels.includes(horizon)
            ? horizon
            : '',
        variant,
      });
    });

    // add vertical lines for max of y axis
    isBordered &&
      results.push({
        x: domain[1],
        label: '',
        variant: 'muted',
      });

    return results;
  }, [yScale, bestHorizon]);

  const onMouseMove = (e: MouseEvent<SVGGElement>) => {
    setMouseX(e.nativeEvent.offsetX);
  };

  const numberFormatter = (value: number) =>
    formatPercentage(value, { suffix });

  const y0 = yScale(0);
  const y1 = height * 0.1;
  const suffix = !!yAxisLabel ? '' : isFixedIncome ? 'bps' : '%';
  const hoveredIndex = mouseX && Math.round(xScale.invert(mouseX));
  const hoveredTs = hoveredIndex && ts[hoveredIndex];
  const hoveredSnakeTs = hoveredIndex && snakeTs[hoveredIndex]?.value;
  const showTooltip = isOpen && !!hoveredTs;
  const snakeDataInDomain = snakeTs.slice(0, domain[1] - domain[0] + 1);

  const getDaysLabel = () => {
    if (!hoveredTs || !hoveredIndex) {
      return daysLabel;
    }

    return t('chart:days', {
      count: hoveredTs.relative_idx,
    });
  };

  const daysLabelToDisplay = getDaysLabel();

  return (
    <div
      className={styles.highLowTripleLineChartRoot}
      style={maxHeight ? { minHeight: maxHeight + 50 } : undefined}
      data-testid="high-low-triple-line-chart"
      data-high-low-triple-line-chart-root
      data-clipboard-snapshot
    >
      {showTooltip && (
        <HighLowTripleLineChartTooltip
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          data-testid="high-low-triple-line-chart-tooltip"
          title={daysLabelToDisplay}
          hoveredTs={hoveredTs}
          hoveredSnakeTs={hoveredSnakeTs}
          tooltipLabels={tooltipLabels}
          numberFormatter={numberFormatter}
        />
      )}
      <svg className={styles.chart} ref={svgRef}>
        <ClipAreas
          uniqueId={uniqueId}
          width={width}
          height={height}
          yScale={yScale}
        />
        <g transform={`translate(0, ${y1})`}>
          <g transform={`translate(${plotWidth}, 0)`}>
            <HorizontalGridLines
              yScale={yScale}
              tickSize={plotWidth}
              format={value => formatPercentage(value, { suffix, decimals: 0 })}
              isBordered={isBordered}
              yAxisLabel={yAxisLabel}
              rightAxisWidth={rightAxisWidth}
            />
          </g>
          <VerticalLines
            lines={verticalLines}
            xScale={xScale}
            yScale={yScale}
            orientation={'bottom'}
          />
          <TripleLines
            uniqueId={uniqueId}
            plots={plots}
            xScale={xScale}
            yScale={yScale}
          />
          <CumulativeReturn
            ts={snakeDataInDomain}
            xScale={xScale}
            yScale={yScale}
            id={uniqueId}
          />
          <line
            className={styles.baseLine}
            x1={0}
            x2={plotWidth}
            y1={y0}
            y2={y0}
          />

          {highlightedHorizons?.map(horizon => {
            const tsIndex = horizonToIndex?.get(horizon);
            const dataPoint = tsIndex && ts[tsIndex];

            if (!dataPoint) {
              return null;
            }

            const yHigh = yScale(dataPoint.high);
            const yLow = yScale(dataPoint.low);
            const yMedian = yScale(dataPoint.median);

            return (
              <g
                key={`${uniqueId}-${tsIndex}`}
                transform={`translate(${xScale(tsIndex)}, 0)`}
              >
                <HighLowBar
                  yMedian={yMedian}
                  yLow={yLow}
                  yHigh={yHigh}
                  y0={y0}
                  dataPoint={dataPoint}
                  showMedianLabel={false}
                  enableMouseInteraction={false}
                  numberFormatter={numberFormatter}
                  hasTextWithBg
                />
              </g>
            );
          })}
        </g>
        {showTooltip && (
          <line
            className={styles.dashedLine}
            x1={mouseX}
            x2={mouseX}
            y1={y1}
            y2={y1 + mainHeight}
          />
        )}
        <rect
          className={styles.hoverAreaRect}
          ref={refs.setReference}
          width={plotWidth}
          y={y1}
          height={mainHeight}
          fill="none"
          data-testid="high-low-triple-line-chart-hover-rect"
          {...getReferenceProps({ onMouseMove })}
        />
      </svg>
    </div>
  );
};
